import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { SingleSelectCards } from '../../components/quiz';
import { Button } from '../../components/ui';
import { track, page, EVENTS } from '../../utils/analytics';
import { buildAdhdSignupUrl } from '../../utils/signupUrl';
import { useApp } from '../../contexts/AppContext';
import type { SelectableCardOption } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'intro' | 'quiz' | 'interstitial' | 'loading' | 'email_gate' | 'results';
type TypeKey = 'visual' | 'autopilot' | 'impulsive' | 'burnedout';

interface AnswerEntry {
  optionId: string;
  typeKey: TypeKey;
}

interface QuizOption extends SelectableCardOption {
  typeKey: TypeKey;
}

interface QuizQuestion {
  key: string;
  text: string;
  options: QuizOption[];
  interstitialAfter?: boolean;
}

// ── Questions ─────────────────────────────────────────────────────────────────

const QUESTIONS: QuizQuestion[] = [
  {
    key: 'aspiration',
    text: 'Biggest challenge with managing money?',
    options: [
      { id: 'A', title: "I lose track - nothing feels visible", typeKey: 'visual' },
      { id: 'B', title: "I forget to pay bills or move money", typeKey: 'impulsive' },
      { id: 'C', title: "I spend before I realize I shouldn't", typeKey: 'impulsive' },
      { id: 'D', title: "I know what to do. I just never do it", typeKey: 'burnedout' },
    ],
    interstitialAfter: true,
  },
  {
    key: 'brain_style',
    text: 'What best describes you?',
    options: [
      { id: 'A', title: 'I need to see it to believe it - out of sight is out of mind', typeKey: 'visual' },
      { id: 'B', title: 'I make impulsive decisions I immediately regret', typeKey: 'impulsive' },
      { id: 'C', title: 'I burn out fast - I do everything at once, then nothing', typeKey: 'burnedout' },
      { id: 'D', title: 'I run on autopilot and only notice the damage later', typeKey: 'autopilot' },
    ],
    interstitialAfter: true,
  },
  {
    key: 'current_system',
    text: 'How do you manage money right now?',
    options: [
      { id: 'A', title: 'Tracking app or spreadsheet', typeKey: 'burnedout' },
      { id: 'B', title: 'Check my balance and hope for the best', typeKey: 'impulsive' },
      { id: 'C', title: 'Auto-transfers set up, still feels scattered', typeKey: 'autopilot' },
      { id: 'D', title: "I don't. I just avoid it", typeKey: 'impulsive' },
    ],
  },
  {
    key: 'income_flow',
    text: 'Money hits your account. What happens?',
    options: [
      { id: 'A', title: 'Sits in one place, I try to split it mentally', typeKey: 'impulsive' },
      { id: 'B', title: 'Some moves automatically, rest is chaos', typeKey: 'autopilot' },
      { id: 'C', title: "Gone before I can plan anything", typeKey: 'impulsive' },
      { id: 'D', title: "I know what I should do. I just don't", typeKey: 'burnedout' },
    ],
    interstitialAfter: true,
  },
  {
    key: 'frustration',
    text: "What's actually making this hard?",
    options: [
      { id: 'A', title: "I spend impulsively and only feel the damage after", typeKey: 'impulsive' },
      { id: 'B', title: "I forget about bills and taxes until it's too late", typeKey: 'impulsive' },
      { id: 'C', title: "I make enough - I'm still stressed all the time", typeKey: 'burnedout' },
      { id: 'D', title: "I can't stick to any system long enough for it to work", typeKey: 'burnedout' },
    ],
  },
  {
    key: 'apps_tried',
    text: 'How many money apps have you tried?',
    options: [
      { id: 'A', title: 'One or two - something sort of works', typeKey: 'autopilot' },
      { id: 'B', title: "Several - I quit after a week, every time", typeKey: 'burnedout' },
      { id: 'C', title: "I've lost count. Nothing has ever fit", typeKey: 'burnedout' },
      { id: 'D', title: 'None - apps make it worse', typeKey: 'visual' },
    ],
    interstitialAfter: true,
  },
  {
    key: 'money_stress',
    text: 'Money stress, for you, feels like...',
    options: [
      { id: 'A', title: "Anxiety that never fully turns off", typeKey: 'burnedout' },
      { id: 'B', title: "Dread every time I open my bank app", typeKey: 'impulsive' },
      { id: 'C', title: 'Shame for being bad at something basic', typeKey: 'burnedout' },
      { id: 'D', title: "Everyone has this figured out except me", typeKey: 'burnedout' },
    ],
  },
];

const TOTAL_QUESTIONS = QUESTIONS.length;

// ── Interstitials ─────────────────────────────────────────────────────────────

interface QuoteInterstitial {
  kind: 'quote';
  quote: string;
  author: string;
}

interface EchoInterstitial {
  kind: 'echo';
  stat: string;
  message: string;
  // Optional per-answer overrides keyed by option ID
  conditional?: Record<string, { stat: string; message: string }>;
}

type Interstitial = QuoteInterstitial | EchoInterstitial;

const INTERSTITIALS: Record<number, Interstitial> = {
  1: {
    kind: 'echo',
    stat: '73%',
    message: 'of people with ADHD say managing money is their biggest daily struggle.',
  },
  2: {
    kind: 'quote',
    quote: '"I don\'t see it, I forget. I don\'t know what\'s coming and going. I\'m just like, oh, we need this - and then it\'s gone."',
    author: 'Sadie, business owner with ADHD',
  },
  4: {
    kind: 'echo',
    stat: '82%',
    message: 'of our users suffered the same experience.',
    conditional: {
      B: { stat: "You're 80% there.", message: "Let's fix the remaining 20%." },
    },
  },
  6: {
    kind: 'quote',
    quote: '"YNAB needs you to show up. Excel needs you to follow up. I was about to have an aneurysm before I found Sequence."',
    author: 'Dylan, self-employed Sequence user',
  },
};

// ── Result types ──────────────────────────────────────────────────────────────

interface Benefit {
  bold: string;
  text: string;
}

interface TypeResult {
  name: string;
  h2: string;
  insight: string;
  benefits: Benefit[];
  testimonial: { quote: string; author: string };
  ctaHeadline: string;
  ctaSub: string;
}

const TYPE_RESULTS: Record<TypeKey, TypeResult> = {
  visual: {
    name: 'The Visual Thinker',
    h2: "Your brain maps money. It doesn't track it.",
    insight: "You process the world visually. Spreadsheets and lists don't just fail to motivate you - they actively work against how your brain functions. You need to SEE your money as a connected flow, not a grid of numbers.",
    benefits: [
      {
        bold: 'A map, not a spreadsheet',
        text: 'Sequence shows your entire money system as a visual flow - income in, pods routing it automatically, accounts receiving it.',
      },
      {
        bold: 'Your money moves before you can spend it',
        text: 'Sequence auto-routes your income into separate buckets the moment it lands. Out of sight means out of reach.',
      },
      {
        bold: 'Set once, see always',
        text: 'You build your map once. After that, it just runs.',
      },
    ],
    testimonial: {
      quote: '"I\'m looking at my map right now. For my ADHD brain, the buckets and seeing everything on the map is super appealing. This is exactly what I need."',
      author: 'Emily, Sequence user',
    },
    ctaHeadline: 'Your money map is ready to build.',
    ctaSub: 'See your full financial picture in one place. Set it up once - it runs automatically from there.',
  },
  autopilot: {
    name: 'The Autopilot Seeker',
    h2: "You don't need more discipline. You need less thinking.",
    insight: "You've tried to build systems before. The problem was never your commitment - it was that every system required constant maintenance. Your ideal setup runs itself, without you having to remember anything.",
    benefits: [
      {
        bold: 'Money moves automatically',
        text: 'You define the rules once. Sequence does the routing every time income lands. No manual transfers. Ever.',
      },
      {
        bold: 'Nothing to log in for',
        text: "Sequence isn't an app you need to check. Your money is already moving the right way.",
      },
      {
        bold: 'Zero discipline required',
        text: 'YNAB needs you to show up weekly. Spreadsheets need you to update them. Sequence assumes none of that. It just moves your money the way you sequence it.',
      },
    ],
    testimonial: {
      quote: '"Most of my accounts I don\'t need to remember to manage. With Sequence the money just goes where it needs to and I stop thinking about it."',
      author: 'Sequence user, ADHD',
    },
    ctaHeadline: 'Set it once. Your money sequences itself.',
    ctaSub: 'No budget to maintain. No transfers to remember. No app to keep opening.',
  },
  impulsive: {
    name: 'The Accidental Spender',
    h2: "You're not bad with money. Your money is in the wrong place.",
    insight: 'When money is visible in your main account, it registers as "available to spend" - even when it\'s mentally earmarked for something else. This isn\'t a willpower problem. It\'s a structural one.',
    benefits: [
      {
        bold: 'Out of sight, out of reach',
        text: 'Sequence moves your taxes, savings, and owner pay into separate accounts before you see the balance. What remains is actually yours to spend.',
      },
      {
        bold: 'No more accidentally robbing Peter to pay Paul',
        text: '"There\'s money in my account, so I spend it - then realize it was already spoken for." Sequence solves this structurally.',
      },
      {
        bold: "Your brain's natural \"safe to spend\" signal actually works",
        text: 'When the money you see is truly available, impulse spending stops being a problem.',
      },
    ],
    testimonial: {
      quote: '"It moves my money into accounts that are out of sight and out of mind. I stop accidentally using funds that were supposed to be saved."',
      author: 'Levi, Sequence user',
    },
    ctaHeadline: "Put your money where you can't accidentally spend it.",
    ctaSub: 'Sequence separates your money automatically - so the balance you see is always the balance you can use.',
  },
  burnedout: {
    name: 'The Burned Out Budgeter',
    h2: "The financial system wasn't built for your brain.",
    insight: "You haven't failed at managing money. Every tool you've tried assumed you'd log in regularly, remember to transfer things, and maintain a habit indefinitely. That's not a you problem. That's a design problem.",
    benefits: [
      {
        bold: 'Systems, not habits',
        text: 'You set up your flow once and it runs without you. The system does the work.',
      },
      {
        bold: 'The problem was never discipline',
        text: 'YNAB needs you to show up. Excel needs you to follow up. Sequence assumes none of that. It just moves your money the way you sequence it.',
      },
      {
        bold: 'Something that actually works with your brain',
        text: "You're not the problem. Every system you've tried was built for a different kind of brain. Sequence was built for yours.",
      },
    ],
    testimonial: {
      quote: '"This is what I need. The visual map, the automation - finally something that works the way I think."',
      author: 'Sequence user',
    },
    ctaHeadline: "You don't need a new habit. You need a system.",
    ctaSub: 'Set up your Sequence map once. Your money moves automatically from there - no maintenance, no memory required.',
  },
};

// ── Loading items ─────────────────────────────────────────────────────────────

const LOADING_ITEMS = [
  'Analyzing your answers',
  'Mapping your money flow',
  'Identifying your pattern',
  'Building your profile',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getWinningType(answers: Record<string, AnswerEntry>): TypeKey {
  const tally: Record<TypeKey, number> = { visual: 0, autopilot: 0, impulsive: 0, burnedout: 0 };
  Object.values(answers).forEach(({ typeKey }) => {
    tally[typeKey]++;
  });
  let winner: TypeKey = 'burnedout';
  let max = -1;
  (Object.keys(tally) as TypeKey[]).forEach((key) => {
    if (tally[key] > max) { max = tally[key]; winner = key; }
  });
  return winner;
}

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Intro illustration ────────────────────────────────────────────────────────

function BrainNetworkIllustration() {
  return (
    <svg width="240" height="172" viewBox="0 0 240 172" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Ambient glow */}
      <ellipse cx="120" cy="86" rx="80" ry="60" fill="rgba(124,58,237,0.18)" />
      <ellipse cx="120" cy="86" rx="50" ry="38" fill="rgba(124,58,237,0.12)" />

      {/* Network lines - outer */}
      <line x1="32" y1="68" x2="78" y2="58" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" />
      <line x1="78" y1="58" x2="120" y2="82" stroke="rgba(167,139,250,0.45)" strokeWidth="1.5" />
      <line x1="120" y1="82" x2="162" y2="58" stroke="rgba(167,139,250,0.45)" strokeWidth="1.5" />
      <line x1="162" y1="58" x2="208" y2="68" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" />
      <line x1="78" y1="58" x2="68" y2="108" stroke="rgba(167,139,250,0.35)" strokeWidth="1.5" />
      <line x1="162" y1="58" x2="172" y2="108" stroke="rgba(167,139,250,0.35)" strokeWidth="1.5" />
      <line x1="120" y1="82" x2="120" y2="128" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" />
      <line x1="68" y1="108" x2="120" y2="128" stroke="rgba(167,139,250,0.35)" strokeWidth="1.5" />
      <line x1="172" y1="108" x2="120" y2="128" stroke="rgba(167,139,250,0.35)" strokeWidth="1.5" />
      {/* Cross lines */}
      <line x1="32" y1="68" x2="68" y2="108" stroke="rgba(167,139,250,0.18)" strokeWidth="1" />
      <line x1="208" y1="68" x2="172" y2="108" stroke="rgba(167,139,250,0.18)" strokeWidth="1" />
      <line x1="78" y1="58" x2="172" y2="108" stroke="rgba(167,139,250,0.12)" strokeWidth="1" />
      <line x1="162" y1="58" x2="68" y2="108" stroke="rgba(167,139,250,0.12)" strokeWidth="1" />

      {/* Dollar nodes - outer corners */}
      <circle cx="32" cy="68" r="16" fill="#2d1b69" stroke="rgba(167,139,250,0.5)" strokeWidth="1.5" />
      <text x="32" y="73" textAnchor="middle" fill="#c4b5fd" fontSize="14" fontWeight="700" fontFamily="sans-serif">$</text>

      <circle cx="208" cy="68" r="16" fill="#2d1b69" stroke="rgba(167,139,250,0.5)" strokeWidth="1.5" />
      <text x="208" y="73" textAnchor="middle" fill="#c4b5fd" fontSize="14" fontWeight="700" fontFamily="sans-serif">$</text>

      <circle cx="120" cy="128" r="16" fill="#2d1b69" stroke="rgba(167,139,250,0.5)" strokeWidth="1.5" />
      <text x="120" y="133" textAnchor="middle" fill="#c4b5fd" fontSize="14" fontWeight="700" fontFamily="sans-serif">$</text>

      {/* Mid-ring nodes */}
      <circle cx="78" cy="58" r="10" fill="#4c1d95" stroke="rgba(196,181,253,0.45)" strokeWidth="1.5" />
      <circle cx="162" cy="58" r="10" fill="#4c1d95" stroke="rgba(196,181,253,0.45)" strokeWidth="1.5" />
      <circle cx="68" cy="108" r="10" fill="#4c1d95" stroke="rgba(196,181,253,0.45)" strokeWidth="1.5" />
      <circle cx="172" cy="108" r="10" fill="#4c1d95" stroke="rgba(196,181,253,0.45)" strokeWidth="1.5" />

      {/* Central glowing hub */}
      <circle cx="120" cy="82" r="22" fill="#5b21b6" />
      <circle cx="120" cy="82" r="22" fill="rgba(139,92,246,0.35)" />
      <circle cx="120" cy="82" r="14" fill="#7c3aed" />
      <text x="120" y="88" textAnchor="middle" fill="white" fontSize="16" fontWeight="800" fontFamily="sans-serif">$</text>
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdhdQuiz() {
  const { setFlow } = useApp();

  const [phase, setPhase] = useState<Phase>('intro');
  const [currentQ, setCurrentQ] = useState(1);
  const [answers, setAnswers] = useState<Record<string, AnswerEntry>>({});
  const [currentSelection, setCurrentSelection] = useState<string | null>(null);
  const [activeInterstitial, setActiveInterstitial] = useState<number | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [typeKey, setTypeKey] = useState<TypeKey>('burnedout');
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  const utmRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'utm_landing_page'];
    const utms: Record<string, string> = {};
    utmKeys.forEach((key) => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    if (!utms.utm_landing_page) utms.utm_landing_page = 'adhd';
    utmRef.current = utms;

    setFlow('adhd');
    page('ADHD Quiz');
    track(EVENTS.ADHD_QUIZ_SHOWN, { flow: 'adhd', device: getDevice(), ...utms });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [currentQ, phase]);

  // Restore selection when navigating between questions
  useEffect(() => {
    if (phase !== 'quiz') return;
    const key = QUESTIONS[currentQ - 1].key;
    setCurrentSelection(answers[key]?.optionId ?? null);
  }, [currentQ, phase]);

  // Loading animation
  useEffect(() => {
    if (phase !== 'loading') return;
    setLoadingStep(0);
    const timers = [
      setTimeout(() => setLoadingStep(1), 700),
      setTimeout(() => setLoadingStep(2), 1500),
      setTimeout(() => setLoadingStep(3), 2300),
      setTimeout(() => setLoadingStep(4), 3100),
      setTimeout(() => {
        const winner = getWinningType(answers);
        setTypeKey(winner);
        setPhase('email_gate');
      }, 3800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  // Fire result shown event
  useEffect(() => {
    if (phase !== 'results') return;
    track(EVENTS.ADHD_RESULT_SHOWN, {
      flow: 'adhd',
      type: typeKey,
      typeName: TYPE_RESULTS[typeKey].name,
      device: getDevice(),
      ...utmRef.current,
    });
  }, [phase, typeKey]);

  function handleOptionSelect(optionId: string) {
    setCurrentSelection(optionId);
  }

  function handleContinue() {
    if (!currentSelection) return;

    const question = QUESTIONS[currentQ - 1];
    const option = question.options.find((o) => o.id === currentSelection);
    if (!option) return;

    const newAnswers: Record<string, AnswerEntry> = {
      ...answers,
      [question.key]: { optionId: currentSelection, typeKey: option.typeKey },
    };
    setAnswers(newAnswers);

    if (currentQ === 1) {
      track(EVENTS.ADHD_QUIZ_STARTED, {
        flow: 'adhd',
        device: getDevice(),
        ...utmRef.current,
      });
    }

    track(EVENTS.ADHD_QUESTION_ANSWERED, {
      flow: 'adhd',
      questionNumber: currentQ,
      questionKey: question.key,
      answerId: currentSelection,
      typeSignal: option.typeKey,
      device: getDevice(),
      ...utmRef.current,
    });

    if (currentQ === TOTAL_QUESTIONS) {
      const winner = getWinningType(newAnswers);
      track(EVENTS.ADHD_QUIZ_COMPLETED, {
        flow: 'adhd',
        type: winner,
        typeName: TYPE_RESULTS[winner].name,
        answers: Object.fromEntries(
          Object.entries(newAnswers).map(([k, v]) => [k, v.optionId])
        ),
        device: getDevice(),
        ...utmRef.current,
      });
      setPhase('loading');
    } else if (question.interstitialAfter) {
      setActiveInterstitial(currentQ);
      setPhase('interstitial');
    } else {
      setCurrentQ((prev) => prev + 1);
      setCurrentSelection(null);
    }
  }

  function handleInterstitialContinue() {
    setPhase('quiz');
    setActiveInterstitial(null);
    setCurrentQ((prev) => prev + 1);
    setCurrentSelection(null);
  }

  function handleBack() {
    if (phase === 'interstitial') {
      setPhase('quiz');
      setActiveInterstitial(null);
      const prevAnswer = answers[QUESTIONS[currentQ - 1].key];
      setCurrentSelection(prevAnswer?.optionId ?? null);
      return;
    }
    if (phase === 'quiz') {
      if (currentQ === 1) {
        setPhase('intro');
        setCurrentSelection(null);
        return;
      }
      setCurrentQ((prev) => prev - 1);
      setCurrentSelection(null);
    }
  }

  const progressPct =
    phase === 'loading' || phase === 'results' ? 100
    : phase === 'intro' ? 0
    : phase === 'interstitial' ? Math.round((currentQ / TOTAL_QUESTIONS) * 100)
    : Math.round(((currentQ - 1) / TOTAL_QUESTIONS) * 100);

  function ProgressHeader({ showBack = false }: { showBack?: boolean }) {
    return (
      <div className="rental-layout__progress">
        <div className="rental-progress">
          <div className="rental-progress__bar">
            <div className="rental-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="rental-progress__meta">
            <span className="rental-progress__pct">{progressPct}%</span>
          </div>
        </div>
        {showBack && (
          <button className="rental-back-btn" onClick={handleBack} aria-label="Go back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>
        )}
      </div>
    );
  }

  // ── Render: intro ─────────────────────────────────────────────────────────────

  if (phase === 'intro') {
    return (
      <>
        <Helmet>
          <title>Find Out How You Can Handle Money Without Losing Focus | Sequence</title>
          <meta name="description" content="7 questions to discover your ADHD money type and get a personalized system that works with your brain - not against it." />
        </Helmet>
        <div className="adhd-intro adhd-intro--dark">
          <div className="adhd-intro__container">
            <div className="adhd-intro__content">
              <div className="adhd-intro__badge adhd-intro__badge--dark">12,000+ ADHD adults · 2 min · Free</div>
              <BrainNetworkIllustration />
              <h1 className="adhd-intro__headline adhd-intro__headline--dark">
                Is your money system built for your brain?
              </h1>
              <p className="adhd-intro__subheadline adhd-intro__subheadline--dark">
                Most financial tools assume you'll remember, log in, and follow through. Find out why that's not working - and what to do instead.
              </p>
              <div className="adhd-intro__testimonial">
                <div className="adhd-intro__testimonial-mark" aria-hidden="true">"</div>
                <p className="adhd-intro__testimonial-quote">
                  For my ADHD brain, the buckets and seeing everything on the map is super appealing. This is exactly what I need.
                </p>
                <p className="adhd-intro__testimonial-author">Emily, Sequence user</p>
              </div>
              <div className="adhd-intro__cta-wrap">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    track(EVENTS.ADHD_QUIZ_CTA_CLICKED, {
                      flow: 'adhd',
                      ctaLocation: 'intro',
                      device: getDevice(),
                      ...utmRef.current,
                    });
                    setPhase('quiz');
                    setCurrentQ(1);
                    setCurrentSelection(null);
                  }}
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  }
                  iconPosition="right"
                >
                  Find out what your brain needs
                </Button>
              </div>
              <p className="adhd-intro__meta adhd-intro__meta--dark">7 questions · Personalized results · No email required</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: interstitial ──────────────────────────────────────────────────────

  if (phase === 'interstitial' && activeInterstitial !== null) {
    const interstitial = INTERSTITIALS[activeInterstitial];

    // Resolve echo content (apply conditional override if applicable)
    let echoContent: { stat: string; message: string } | null = null;
    if (interstitial.kind === 'echo') {
      const prevAnswer = answers[QUESTIONS[activeInterstitial - 1].key];
      const override = interstitial.conditional?.[prevAnswer?.optionId ?? ''];
      echoContent = override ?? { stat: interstitial.stat, message: interstitial.message };
    }

    const continueArrow = (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    );

    return (
      <>
        <Helmet>
          <title>Is Your Money System Built for Your Brain? | Sequence</title>
        </Helmet>
        <div className="rental-layout">
          <ProgressHeader showBack />
          <main className="rental-layout__content">
            <div className="rental-layout__container">
              <div className="adhd-interstitial">

                {interstitial.kind === 'quote' ? (
                  <div className="adhd-interstitial__card">
                    <div className="adhd-interstitial__mark" aria-hidden="true">"</div>
                    <blockquote className="adhd-interstitial__quote">{interstitial.quote}</blockquote>
                    <p className="adhd-interstitial__author">- {interstitial.author}</p>
                  </div>
                ) : (
                  <div className="adhd-echo">
                    <div className="adhd-echo__stat">{echoContent!.stat}</div>
                    <p className="adhd-echo__message">{echoContent!.message}</p>
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleInterstitialContinue}
                  icon={continueArrow}
                  iconPosition="right"
                >
                  Continue
                </Button>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  // ── Render: loading ───────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <>
        <Helmet>
          <title>Building Your Money Map... | Sequence</title>
        </Helmet>
        <div className="adhd-loading">
          <div className="adhd-loading__container">
            <div className="adhd-loading__spinner" aria-hidden="true" />
            <h1 className="adhd-loading__title">Evaluating your finance score</h1>
            <div className="adhd-loading__checklist">
              {LOADING_ITEMS.map((item, i) => {
                const done = i < loadingStep - 1;
                const active = i === loadingStep - 1;
                return (
                  <div
                    key={i}
                    className={`adhd-loading__item${
                      done ? ' adhd-loading__item--done'
                      : active ? ' adhd-loading__item--active'
                      : i < loadingStep ? ' adhd-loading__item--visible'
                      : ''
                    }`}
                  >
                    <span className="adhd-loading__item-dot" />
                    <span className="adhd-loading__item-text">{item}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: email gate ───────────────────────────────────────────────────────

  if (phase === 'email_gate') {
    function handleEmailSubmit(e: React.FormEvent) {
      e.preventDefault();
      if (emailSubmitting) return;
      setEmailSubmitting(true);
      track('ADHD Email Captured', {
        flow: 'adhd',
        email,
        type: typeKey,
        ...utmRef.current,
      });
      setTimeout(() => setPhase('results'), 300);
    }

    function handleEmailSkip() {
      track('ADHD Email Skipped', {
        flow: 'adhd',
        type: typeKey,
        ...utmRef.current,
      });
      setPhase('results');
    }

    return (
      <>
        <Helmet>
          <title>Your Results Are Ready | Sequence</title>
        </Helmet>
        <div className="adhd-email-gate">
          <div className="adhd-email-gate__container">
            <div className="adhd-email-gate__icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h1 className="adhd-email-gate__headline">Your brain type is ready</h1>
            <p className="adhd-email-gate__sub">
              Drop your email to get your results - plus tips built for your brain type.
            </p>
            <form className="adhd-email-gate__form" onSubmit={handleEmailSubmit}>
              <input
                className="adhd-email-gate__input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
              />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={!email || emailSubmitting}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                {emailSubmitting ? 'Loading...' : 'See my results'}
              </Button>
            </form>
            <button className="adhd-email-gate__skip" onClick={handleEmailSkip}>
              Skip - show me my results
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Render: results ───────────────────────────────────────────────────────────

  if (phase === 'results') {
    const result = TYPE_RESULTS[typeKey];
    const signupUrl = buildAdhdSignupUrl({ type: typeKey, utms: utmRef.current });

    return (
      <>
        <Helmet>
          <title>Your Money Brain Type | Sequence</title>
        </Helmet>
        <div className="adhd-results">

          {/* Hero */}
          <div className="adhd-results__hero">
            <div className="adhd-results__hero-inner">
              <div className="adhd-results__type-badge">You are:</div>
              <h1 className="adhd-results__type-name">{result.name}</h1>
              <h2 className="adhd-results__h2">{result.h2}</h2>
              <p className="adhd-results__insight">{result.insight}</p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  track(EVENTS.SEQUENCE_SIGNUP_CLICKED, {
                    flow: 'adhd',
                    type: typeKey,
                    typeName: result.name,
                    ctaLocation: 'hero',
                    ...utmRef.current,
                  });
                  window.location.href = signupUrl;
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Start sequencing my money
              </Button>
            </div>
          </div>

          {/* Benefits */}
          <div className="adhd-results__benefits">
            <div className="adhd-results__benefits-inner">
              <div className="adhd-results__benefits-header">
                <p className="adhd-results__benefits-eyebrow">How Sequence fixes this</p>
                <h2 className="adhd-results__benefits-heading">Built for brains like yours</h2>
              </div>
              <div className="adhd-results__benefits-grid">
                {result.benefits.map((benefit, i) => (
                  <div key={i} className="adhd-results__benefit-card">
                    <div className="adhd-results__benefit-check" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p className="adhd-results__benefit-bold">{benefit.bold}</p>
                    <p className="adhd-results__benefit-text">{benefit.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Social proof */}
          <div className="adhd-results__social">
            <div className="adhd-results__social-inner">
              <p className="adhd-results__social-label">What users say</p>
              <div className="adhd-results__social-grid">
                <div className="adhd-results__social-card">
                  <p className="adhd-results__social-quote">"I was about to have an aneurysm with my manual tracking spreadsheets. Whatever you guys are doing, you're literally doing God's work."</p>
                  <div className="adhd-results__social-byline">Dylan E. - Sequence user</div>
                </div>
                <div className="adhd-results__social-card">
                  <p className="adhd-results__social-quote">"For my ADHD brain, the buckets and seeing everything on the map is super appealing. This is exactly what I need."</p>
                  <div className="adhd-results__social-byline">Emily - Sequence user</div>
                </div>
                <div className="adhd-results__social-card">
                  <p className="adhd-results__social-quote">"It moves my money into accounts that are out of sight and out of mind. I stop accidentally using funds that were supposed to be saved."</p>
                  <div className="adhd-results__social-byline">Levi - Sequence user</div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="adhd-results__cta">
            <div className="adhd-results__cta-inner">
              <p className="adhd-results__cta-eyebrow">Your money map is ready</p>
              <h2 className="adhd-results__cta-headline">{result.ctaHeadline}</h2>
              <p className="adhd-results__cta-sub">{result.ctaSub}</p>
              <Button
                variant="primary"
                size="lg"
                className="adhd-results__cta-btn"
                onClick={() => {
                  track(EVENTS.SEQUENCE_SIGNUP_CLICKED, {
                    flow: 'adhd',
                    type: typeKey,
                    typeName: result.name,
                    ctaLocation: 'bottom',
                    ...utmRef.current,
                  });
                  window.location.href = signupUrl;
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Start sequencing my money
              </Button>
              <p className="adhd-results__cta-guarantee">30-day money-back guarantee - bank-level security via Plaid</p>
            </div>
          </div>

        </div>
      </>
    );
  }

  // ── Render: quiz question ─────────────────────────────────────────────────────

  const question = QUESTIONS[currentQ - 1];
  const cardOptions: SelectableCardOption[] = question.options.map((o) => ({
    id: o.id,
    title: o.title,
  }));

  return (
    <>
      <Helmet>
        <title>Is Your Money System Built for Your Brain? | Sequence</title>
      </Helmet>
      <div className="rental-layout adhd-quiz-layout">
        <ProgressHeader showBack />
        <main className="rental-layout__content">
          <div className="rental-layout__container">
            <div className="quiz-step">
              <div className="quiz-step__header">
                <p className="adhd-quiz__step-label">Question {currentQ} of {TOTAL_QUESTIONS}</p>
                <h2 className="quiz-step__question">{question.text}</h2>
              </div>
              <div className="quiz-step__body">
                <SingleSelectCards
                  options={cardOptions}
                  value={currentSelection}
                  onChange={handleOptionSelect}
                />
              </div>
            </div>
          </div>
        </main>
        <div className="adhd-quiz__continue-bar">
          <div className="adhd-quiz__continue-inner">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!currentSelection}
              onClick={handleContinue}
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              }
              iconPosition="right"
            >
              {currentSelection ? 'Continue' : 'Pick an answer to continue'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
